import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';

export type DividerOrientation = 'horizontal' | 'vertical';
export type DividerVariant = 'subtle' | 'strong';
export type DividerInset = 'none' | 'start' | 'end' | 'both';

const LINE: Record<DividerVariant, string> = {
  // `--border-subtle` is documented in tokens.css as a decorative separator and
  // deliberately not held to the 3:1 control-boundary bar that `--border` is.
  subtle: 'bg-line-subtle',
  strong: 'bg-line',
};

const INSET_HORIZONTAL: Record<DividerInset, string> = {
  none: '',
  start: 'ml-4',
  end: 'mr-4',
  both: 'mx-4',
};

const INSET_VERTICAL: Record<DividerInset, string> = {
  none: '',
  start: 'mt-4',
  end: 'mb-4',
  both: 'my-4',
};

export interface DividerProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  orientation?: DividerOrientation;
  variant?: DividerVariant;
  /** Keeps a gap at the start, the end, or both — for aligning against a row's padding. */
  inset?: DividerInset;
  /** Renders the label centred in the line. Horizontal only. */
  label?: ReactNode;
}

/**
 * A static structural rule. CONVENTIONS §8 names the divider explicitly as a
 * component that never carries motion, so this file imports no animation
 * library: the only states are colour and inset.
 */
export function Divider({
  orientation = 'horizontal',
  variant = 'subtle',
  inset = 'none',
  label,
  className,
  ...rest
}: DividerProps) {
  if (orientation === 'vertical') {
    return (
      <div
        {...rest}
        role="separator"
        aria-orientation="vertical"
        className={cn('w-px shrink-0 self-stretch', LINE[variant], INSET_VERTICAL[inset], className)}
      />
    );
  }

  if (label) {
    return (
      <div
        {...rest}
        role="separator"
        aria-orientation="horizontal"
        className={cn('flex w-full items-center gap-3', INSET_HORIZONTAL[inset], className)}
      >
        <span aria-hidden="true" className={cn('h-px min-w-0 flex-1', LINE[variant])} />
        <span className="shrink-0 font-display text-2xs font-semibold uppercase tracking-label text-muted">
          {label}
        </span>
        <span aria-hidden="true" className={cn('h-px min-w-0 flex-1', LINE[variant])} />
      </div>
    );
  }

  return (
    <div
      {...rest}
      role="separator"
      aria-orientation="horizontal"
      className={cn('h-px w-full', LINE[variant], INSET_HORIZONTAL[inset], className)}
    />
  );
}
